/**
 * The cold-load sequence (docs/03 §5.1 and §5.2).
 *
 * ```
 * wave A, all parallel, one pooled connection:
 *   GET /config
 *   GET /markets/current?symbol=…&include=orderbook,trades,candles
 *   GET /index/{symbol}
 *   GET /series
 *   GET /series/{id}/markets?state=…&limit=6
 *   GET /auth/session
 *   GET /time  × 10, serial, ~220ms apart
 *
 * wave B, only if wave A resolved a session:
 *   GET /portfolio/balance · /portfolio/positions · /orders?status=OPEN
 *   GET /notifications?unread=true&limit=20
 * ```
 *
 * Three rules shape the control flow, and each one is a decision rather than a
 * style:
 *
 * 1. **Nothing waits on the socket.** If the WebSocket never opens, the page is
 *    a complete and correct static snapshot behind a `STALE` pill (§9.2.1).
 *    Boot does not open one and does not know it exists.
 * 2. **Nothing waits on `/auth/session`.** The market page is anonymous by
 *    design and renders identically either way; the header swaps `Connect` for
 *    the balance chip when the session resolves. That is the single dependency
 *    edge in the whole cold load, and it is off the critical path.
 * 3. **Nothing waits on the clock.** The ten `/time` samples take about 2.3
 *    seconds — far longer than first meaningful paint — so the countdown renders
 *    provisionally and commits when the median offset lands (D9). Blocking paint
 *    on the clock would trade the whole cold-load budget for a correction of a
 *    few tens of milliseconds.
 *
 * The critical path is therefore document → JS → `markets/current` → paint:
 * three serial hops, everything else parallel or after.
 */

import { syncClock, type Clock, type HandshakeResult, type TimeFetcher } from '@/lib/clock'
import { COLD_LOAD_SAMPLES, COLD_LOAD_SPREAD_MS } from '@/lib/clock'
import type { Api } from '@/api/http/endpoints'
import { assetForSymbol, seriesIdFor, type Symbol_ } from '@/lib/market-id'

export interface BootOptions {
  api: Api
  clock: Clock
  /** The asset the user landed on. `/btc` is the default landing page. */
  symbol?: Symbol_
  signal?: AbortSignal
  /** Ten at cold load; fewer only in tests that do not care about the median. */
  clockSamples?: number
  clockSpreadMs?: number
  /** Injected so a headless run does not spend two seconds of real time. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  wallNow?: () => number
  /** Resolve wave B in-line rather than leaving it to the caller. */
  loadPrivate?: boolean
}

export interface BootFailure {
  step: string
  error: unknown
}

/** Derived from the endpoint functions rather than restated, so a spec change
 *  reaches this shape without anyone remembering to edit it twice. */
type Returns<F> = F extends (...args: never[]) => Promise<infer R> ? R : never

export interface PublicSnapshot {
  config: Returns<Api['meta']['getExchangeConfig']>
  currentMarkets: Returns<Api['markets']['getCurrentMarkets']>
  series: Returns<Api['markets']['listSeries']> | null
  seriesMarkets: Returns<Api['markets']['listSeriesMarkets']> | null
  index: Returns<Api['index']['getIndexPrice']> | null
}

export interface PrivateSnapshot {
  balance: Returns<Api['portfolio']['getBalance']> | null
  positions: Returns<Api['portfolio']['listPositions']> | null
  openOrders: Returns<Api['trading']['listOrders']> | null
  notifications: Returns<Api['notifications']['listNotifications']> | null
}

export interface BootResult {
  symbol: Symbol_
  seriesId: string
  public: PublicSnapshot
  /** `null` when `/auth/session` failed or returned no account. */
  session: Returns<Api['auth']['getSession']> | null
  authenticated: boolean
  private: PrivateSnapshot | null
  /**
   * Resolves when the ten-sample handshake has been applied. Deliberately a
   * promise on the result rather than something boot awaited: the countdown
   * must render before this settles.
   */
  clockSync: Promise<HandshakeResult>
  /** Non-fatal failures — every optional call that did not answer. */
  failures: BootFailure[]
}

/** The rail shows the neighbourhood, not the archive (§3.3). */
/**
 * What `GET /markets/current` is asked to carry, on every path that calls it.
 *
 * **All five members, spelled out, and sent as five repeated parameters.**
 * `include` is `style: form, explode: true`, so the comma-joined
 * `include=orderbook,trades,candles` this client sent on the reconnect path
 * matched nothing and was answered `200` with all three payloads absent —
 * §9.2.5 repairing the market strip and nothing else, invisibly (docs/17, §F48).
 *
 * `previous` and `next` are in the list because the parameter's default is
 * `[previous, next]` *when it is absent* — sending only the three payload
 * members is asking for the payloads and **not** the neighbours, and
 * `previous` is the market `<ResolutionPanel>` renders. A server that reads
 * the parameter strictly would be right to withhold them, and one of them now
 * does.
 *
 * One constant, because the cold load, the reconnect-and-wake reconcile and
 * the asset switch must ask for the same thing: three call sites drifting is
 * how a screen ends up depending on a field one of them never requested.
 */
export const STRIP_INCLUDE = ['previous', 'next', 'orderbook', 'trades', 'candles'] as const

/**
 * The rail's states, and the two terminal ones are the point.
 *
 * `<SeriesRail>` shows two markets **behind** the live one — the ones whose
 * outcomes the user just watched — and those are `RESOLVED` or `SETTLED` by the
 * time anybody reads them. Asking for only the four live-ish states meant the
 * rail's past half could never be seeded: the resolved-previous chip arrived
 * only on the `series.{id}` snapshot, which carries one, and the chip before it
 * arrived never. The limit is `RAIL_SLOTS`, which is what the window renders.
 */
export const SERIES_RAIL_STATES = [
  'SCHEDULED',
  'FIXING',
  'OPEN',
  'RESOLVING',
  'RESOLVED',
  'SETTLED',
] as const
export const SERIES_RAIL_LIMIT = 6

/**
 * **The chart's candles are deliberately *not* a wave-A call.**
 *
 * An earlier version of this file fetched `GET /index/{symbol}/candles` here,
 * to seed a line chart that accumulated 5 Hz ticks. §F19 replaced that chart
 * with candlesticks over a timeframe ladder, and history is now fetched per
 * (symbol, timeframe) by the panel that draws it — so a wave-A copy would be a
 * second request for bytes the chart would immediately replace.
 *
 * Which restores what §5.1 says: **seven wave-A calls**, three serial hops to
 * first meaningful paint. The chart fills a moment later, off the critical path,
 * as a chart should.
 */

export class BootError extends Error {
  override readonly name = 'BootError'
  readonly step: string
  constructor(step: string, cause: unknown) {
    super(`cold load failed at ${step}`, { cause })
    this.step = step
  }
}

export async function boot(options: BootOptions): Promise<BootResult> {
  const { api, clock } = options
  const symbol = options.symbol ?? 'BTC-USD'
  const seriesId = seriesIdForSymbol(symbol)
  const failures: BootFailure[] = []
  const signal = options.signal

  // The clock handshake starts first and is never awaited here. It runs
  // alongside every request below, on the same warm connection.
  const clockSync = syncClock(clock, timeFetcherFor(api), {
    samples: options.clockSamples ?? COLD_LOAD_SAMPLES,
    spreadMs: options.clockSpreadMs ?? COLD_LOAD_SPREAD_MS,
    ...(options.sleep ? { sleep: options.sleep } : {}),
    ...(options.wallNow ? { wallNow: options.wallNow } : {}),
    ...(signal ? { signal } : {}),
  })
  // Nobody may await this later and find an unhandled rejection waiting.
  clockSync.catch(() => {})

  // Wave A. `allSettled`, not `all`: one optional call failing must not cost
  // the page its book, and a rejection in a sibling must not be left unhandled
  // while the first rejection propagates.
  const [configR, currentMarketsR, seriesR, seriesMarketsR, indexR, sessionR] = await Promise.allSettled([
    api.meta.getExchangeConfig({ ...opt(signal) }),
    api.markets.getCurrentMarkets({ symbol: [symbol], include: [...STRIP_INCLUDE] }, { ...opt(signal) }),
    api.markets.listSeries({ ...opt(signal) }),
    api.markets.listSeriesMarkets(
      seriesId,
      { state: [...SERIES_RAIL_STATES], limit: SERIES_RAIL_LIMIT },
      { ...opt(signal) },
    ),
    api.index.getIndexPrice(symbol, { ...opt(signal) }),
    // Anonymous is a first-class outcome here, not a failure to recover from.
    api.auth.getSession({ ...opt(signal) }),
  ])

  // Two of the six are required, and for different reasons. Without `/config`
  // every disabled-button reason in the ticket is a guess (§1.6); without
  // `markets/current` there is no market to render. The other four degrade.
  const config = required('GET /config', configR)
  const currentMarkets = required('GET /markets/current', currentMarketsR)
  const series = optional('GET /series', seriesR, failures)
  const seriesMarkets = optional('GET /series/{id}/markets', seriesMarketsR, failures)
  const index = optional('GET /index/{symbol}', indexR, failures)
  const session = optional('GET /auth/session', sessionR, failures)

  const authenticated = isAuthenticated(session)

  const result: BootResult = {
    symbol,
    seriesId,
    public: { config, currentMarkets, series, seriesMarkets, index },
    session,
    authenticated,
    private: null,
    clockSync,
    failures,
  }

  if (authenticated && (options.loadPrivate ?? true)) {
    result.private = await loadPrivateSnapshot(api, failures, signal)
  }

  return result
}

/**
 * Wave B. Fires only once `/auth/session` has resolved an account, and is never
 * on the critical path for trading: the ticket is usable the moment `/config`
 * and `markets/current` land, and disables itself afterwards if the balance
 * turns out to be short — a state change after paint, not a gate before it.
 */
export async function loadPrivateSnapshot(
  api: Api,
  failures: BootFailure[],
  signal?: AbortSignal,
): Promise<PrivateSnapshot> {
  const [balanceR, positionsR, openOrdersR, notificationsR] = await Promise.allSettled([
    api.portfolio.getBalance({ ...opt(signal) }),
    api.portfolio.listPositions(undefined, { ...opt(signal) }),
    api.trading.listOrders({ status: ['OPEN'] }, { ...opt(signal) }),
    api.notifications.listNotifications({ unread_only: true, limit: 20 }, { ...opt(signal) }),
  ])
  return {
    balance: optional('GET /portfolio/balance', balanceR, failures),
    positions: optional('GET /portfolio/positions', positionsR, failures),
    openOrders: optional('GET /orders?status=OPEN', openOrdersR, failures),
    notifications: optional('GET /notifications', notificationsR, failures),
  }
}

/** Adapts `GET /time` to the shape `lib/clock` asks for, so `lib/` keeps
 *  importing nothing from `api/`. */
export function timeFetcherFor(api: Api): TimeFetcher {
  return async (clientSentMs, signal) => {
    const reading = await api.meta.getServerTime(
      { client_sent_ms: clientSentMs },
      // Never cached, never coalesced: two samples that share a response are
      // one sample with the error counted twice.
      { noCache: true, ...opt(signal) },
    )
    return {
      server_time_ms: reading.server_time_ms,
      ...(reading.server_recv_ms === undefined || reading.server_recv_ms === null
        ? {}
        : { server_recv_ms: reading.server_recv_ms }),
    }
  }
}

function seriesIdForSymbol(symbol: Symbol_): string {
  return seriesIdFor(assetForSymbol(symbol))
}

/**
 * One boolean, and deliberately so.
 *
 * `GET /auth/session` answers `200 {authenticated: false}` for an anonymous
 * visitor rather than `401` (docs/06 §F21a), so the majority cold-load path is
 * not an error and does not enter `failures`. The `address` fallback covers a
 * server that has not yet grown the field; it is not the primary branch, because
 * branching on the presence of an optional property is how a partial response
 * gets read as a session.
 */
function isAuthenticated(session: Returns<Api['auth']['getSession']> | null): boolean {
  if (!session) return false
  const value = session as { authenticated?: unknown; address?: unknown }
  if (typeof value.authenticated === 'boolean') return value.authenticated
  return typeof value.address === 'string' && value.address.length > 0
}

function required<T>(step: string, result: PromiseSettledResult<T>): T {
  if (result.status === 'rejected') throw new BootError(step, result.reason)
  return result.value
}

function optional<T>(step: string, result: PromiseSettledResult<T>, failures: BootFailure[]): T | null {
  if (result.status === 'rejected') {
    failures.push({ step, error: result.reason })
    return null
  }
  return result.value
}

function opt(signal: AbortSignal | undefined): { signal?: AbortSignal } {
  return signal ? { signal } : {}
}
